import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Loader2, Briefcase } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const GoogleIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.1A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.44.34-2.1V7.07H2.18A11 11 0 0 0 1 12c0 1.78.43 3.45 1.18 4.93l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
  </svg>
);

export default function Login({ initialMode = "login" }) {
  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post(mode === "login" ? "/auth/login" : "/auth/register", form);
      signIn(data);
      if (next === "pricing") navigate("/pricing", { replace: true });
      else navigate(data.user.company_id ? "/dashboard" : "/workspace", { replace: true });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + (next === "pricing" ? "/pricing" : "/dashboard");
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen hero-gradient flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-[420px]">
        <Link to="/" className="flex items-center gap-2.5 mb-6 justify-center" data-testid="auth-home-link">
          <div className="h-9 w-9 rounded-xl bg-primary flex items-center justify-center"><Briefcase className="h-4 w-4 text-white" /></div>
          <span className="font-display font-semibold text-lg">Virtual CEO</span>
        </Link>
        <div className="bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-sm">
          <Tabs value={mode} onValueChange={(v) => { setMode(v); setError(""); }}>
            <TabsList className="grid grid-cols-2 w-full mb-6">
              <TabsTrigger value="login" data-testid="auth-tab-login">Sign in</TabsTrigger>
              <TabsTrigger value="signup" data-testid="auth-tab-signup">Create account</TabsTrigger>
            </TabsList>
          </Tabs>
          <Button type="button" variant="secondary" className="w-full" onClick={google} data-testid="auth-google-button">
            <GoogleIcon /> <span className="ml-2">Continue with Google</span>
          </Button>
          <div className="flex items-center gap-3 my-5">
            <Separator className="flex-1" /><span className="text-xs text-muted-foreground">or with email</span><Separator className="flex-1" />
          </div>
          <form onSubmit={submit} className="space-y-4">
            {mode === "signup" && (
              <div className="space-y-1.5">
                <Label htmlFor="name">Full name</Label>
                <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="auth-name-input" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email">Work email</Label>
              <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="auth-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="auth-password-input" />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="auth-error">{error}</p>}
            <Button type="submit" className="w-full" disabled={busy} data-testid="auth-submit-button">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {mode === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>
        </div>
        <p className="text-xs text-muted-foreground text-center mt-4">
          To try it out, sign in with <span className="font-mono">admin@virtualceo.app</span> to explore the sample workspace.
        </p>
      </div>
    </div>
  );
}
